import { z } from 'zod';
import {
  FISHERY_GEAR_CATEGORIES,
  FISHERY_PRODUCTION_METHODS,
  type FisheryGearCategory,
  type FisheryProductionMethod,
} from './fishery-origin-catalog';
import {
  isPrepackagedMarketCountryCode,
  type PrepackagedMarketCountryCode,
} from './prepackaged-country-names';
import {
  PREPACKAGED_ORIGIN_AGGREGATES,
  PREPACKAGED_VERTICAL_ORIGIN_REGIMES,
  type PrepackagedBeefOriginEntry,
  type PrepackagedDurumWheatPastaOriginEntry,
  type PrepackagedFisheryOriginEntry,
  type PrepackagedHoneyCountry,
  type PrepackagedMilkDairyOriginEntry,
  type PrepackagedOliveOilOriginEntry,
  type PrepackagedOriginAggregate,
  type PrepackagedOtherFreshMeatOriginEntry,
  type PrepackagedOtherVerticalOriginEntry,
  type PrepackagedProcessedPorkOriginEntry,
  type PrepackagedRiceOriginEntry,
  type PrepackagedTomatoOriginEntry,
  PrepackagedVerticalOriginRegime,
} from './prepackaged-vertical-origin';

type CountryValue = PrepackagedBeefOriginEntry['bornIn'];

export const INGREDIENT_VERTICAL_ORIGIN_FIELDS: Record<
  PrepackagedVerticalOriginRegime,
  readonly string[]
> = {
  BEEF: [
    'bornIn',
    'raisedIn',
    'slaughteredIn',
    'slaughterhouseApprovalNumber',
    'cutIn',
    'cuttingPlantApprovalNumber',
    'minced',
    'preparedIn',
    'thirdCountryInfoUnavailable',
  ],
  OTHER_MEAT_FRESH: [
    'species',
    'raisedIn',
    'slaughteredIn',
    'slaughterAgeMonths',
    'slaughterLiveWeightKg',
    'residenceThresholdMet',
    'mincedOrTrimmings',
    'thirdCountryInfoUnavailable',
  ],
  FISHERY: [
    'scientificName',
    'productionMethod',
    'faoArea',
    'faoSubArea',
    'waterBody',
    'countryCode',
    'gearCategory',
  ],
  HONEY: ['harvestCountries'],
  OLIVE_OIL: ['category', 'oliveHarvestPlace', 'millPlace', 'blend', 'protectedDesignation'],
  IT_MILK_DAIRY: ['milkingCountry'],
  IT_DURUM_WHEAT_PASTA: ['wheatGrowingCountry', 'millingCountry'],
  IT_RICE: ['growingCountry'],
  IT_TOMATO: ['growingCountry'],
  IT_PROCESSED_PORK: ['bornIn', 'raisedIn', 'slaughteredIn'],
  OTHER_REQUIRES_REVIEW: [],
};

export const LOT_VERTICAL_ORIGIN_FIELDS: Partial<
  Record<PrepackagedVerticalOriginRegime, readonly string[]>
> = {
  BEEF: ['referenceCode'],
  OTHER_MEAT_FRESH: ['batchCode'],
};

export interface IngredientBeefVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.BEEF;
  readonly bornIn: CountryValue;
  readonly raisedIn: PrepackagedBeefOriginEntry['raisedIn'];
  readonly slaughteredIn: CountryValue;
  readonly slaughterhouseApprovalNumber: string;
  readonly cutIn: PrepackagedBeefOriginEntry['cutIn'];
  readonly cuttingPlantApprovalNumber: PrepackagedBeefOriginEntry['cuttingPlantApprovalNumber'];
  readonly minced: boolean;
  readonly preparedIn: PrepackagedBeefOriginEntry['preparedIn'];
  readonly thirdCountryInfoUnavailable: boolean;
}

export interface IngredientOtherFreshMeatVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.OTHER_MEAT_FRESH;
  readonly species: PrepackagedOtherFreshMeatOriginEntry['species'];
  readonly raisedIn: PrepackagedOtherFreshMeatOriginEntry['raisedIn'];
  readonly slaughteredIn: CountryValue;
  readonly slaughterAgeMonths: PrepackagedOtherFreshMeatOriginEntry['slaughterAgeMonths'];
  readonly slaughterLiveWeightKg: PrepackagedOtherFreshMeatOriginEntry['slaughterLiveWeightKg'];
  readonly residenceThresholdMet: boolean;
  readonly mincedOrTrimmings: boolean;
  readonly thirdCountryInfoUnavailable: boolean;
}

export interface IngredientFisheryVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.FISHERY;
  readonly scientificName: string;
  readonly productionMethod: FisheryProductionMethod;
  readonly faoArea: PrepackagedFisheryOriginEntry['faoArea'];
  readonly faoSubArea: PrepackagedFisheryOriginEntry['faoSubArea'];
  readonly waterBody: PrepackagedFisheryOriginEntry['waterBody'];
  readonly countryCode: PrepackagedFisheryOriginEntry['countryCode'];
  readonly gearCategory: FisheryGearCategory | null;
}

export interface IngredientHoneyVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.HONEY;
  readonly harvestCountries: readonly PrepackagedHoneyCountry[];
}

export interface IngredientOliveOilVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.OLIVE_OIL;
  readonly category: PrepackagedOliveOilOriginEntry['category'];
  readonly oliveHarvestPlace: PrepackagedOliveOilOriginEntry['oliveHarvestPlace'];
  readonly millPlace: PrepackagedOliveOilOriginEntry['millPlace'];
  readonly blend: PrepackagedOliveOilOriginEntry['blend'];
  readonly protectedDesignation: PrepackagedOliveOilOriginEntry['protectedDesignation'];
}

export interface IngredientMilkDairyVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_MILK_DAIRY;
  readonly milkingCountry: PrepackagedMilkDairyOriginEntry['milkingCountry'];
}

export interface IngredientDurumWheatPastaVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA;
  readonly wheatGrowingCountry: PrepackagedDurumWheatPastaOriginEntry['wheatGrowingCountry'];
  readonly millingCountry: PrepackagedDurumWheatPastaOriginEntry['millingCountry'];
}

export interface IngredientRiceVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_RICE;
  readonly growingCountry: PrepackagedRiceOriginEntry['growingCountry'];
}

export interface IngredientTomatoVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_TOMATO;
  readonly growingCountry: PrepackagedTomatoOriginEntry['growingCountry'];
}

export interface IngredientProcessedPorkVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK;
  readonly bornIn: PrepackagedProcessedPorkOriginEntry['bornIn'];
  readonly raisedIn: PrepackagedProcessedPorkOriginEntry['raisedIn'];
  readonly slaughteredIn: PrepackagedProcessedPorkOriginEntry['slaughteredIn'];
}

export interface IngredientOtherVerticalOriginEntry {
  readonly regime: PrepackagedOtherVerticalOriginEntry['regime'];
}

export type IngredientVerticalOriginEntry =
  | IngredientBeefVerticalOriginEntry
  | IngredientOtherFreshMeatVerticalOriginEntry
  | IngredientFisheryVerticalOriginEntry
  | IngredientHoneyVerticalOriginEntry
  | IngredientOliveOilVerticalOriginEntry
  | IngredientMilkDairyVerticalOriginEntry
  | IngredientDurumWheatPastaVerticalOriginEntry
  | IngredientRiceVerticalOriginEntry
  | IngredientTomatoVerticalOriginEntry
  | IngredientProcessedPorkVerticalOriginEntry
  | IngredientOtherVerticalOriginEntry;

// Codice paese REALE (ISO-3166 alpha-2) o aggregato ammesso dai decreti.
// `z.custom` con la guard del catalogo: l'inferenza coincide con
// `CountryValue` e il confine rifiuta anche testo libero che il DTO
// class-validator lascerebbe passare.
const countryValueSchema = z.union([
  z.custom<PrepackagedMarketCountryCode>((value) => isPrepackagedMarketCountryCode(value)),
  z.custom<PrepackagedOriginAggregate>(
    (value) =>
      typeof value === 'string' &&
      (PREPACKAGED_ORIGIN_AGGREGATES as readonly string[]).includes(value),
  ),
]);
// Pesca e miele dichiarano il solo codice ISO (una zona di cattura o un
// raccolto non può essere "UE"): niente aggregati su questi campi.
const marketCountrySchema = z.custom<PrepackagedMarketCountryCode>((value) =>
  isPrepackagedMarketCountryCode(value),
);
const nullableMarketCountrySchema = marketCountrySchema.nullable();
const countryValuesSchema = z.array(countryValueSchema).max(20);
const nullableCountryValueSchema = countryValueSchema.nullable();
// Limiti speculari al DTO class-validator (ingredient-vertical-origin-entry.dto):
// il confine Zod è l'ultima difesa per i chiamanti che non passano dalla pipe HTTP.
const approvalNumberSchema = z.string().max(40);
const nullableApprovalNumberSchema = approvalNumberSchema.nullable();
const faoAreaSchema = z.string().max(10);
const nullableFaoAreaSchema = faoAreaSchema.nullable();
const waterBodySchema = z.string().max(100);
const protectedDesignationSchema = z.string().max(100);

export const ingredientVerticalOriginEntrySchema = z.discriminatedUnion('regime', [
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.BEEF),
    bornIn: countryValueSchema,
    raisedIn: countryValuesSchema,
    slaughteredIn: countryValueSchema,
    slaughterhouseApprovalNumber: approvalNumberSchema,
    cutIn: nullableCountryValueSchema,
    cuttingPlantApprovalNumber: nullableApprovalNumberSchema,
    minced: z.boolean(),
    preparedIn: nullableCountryValueSchema,
    thirdCountryInfoUnavailable: z.boolean(),
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.OTHER_MEAT_FRESH),
    species: z.enum(['PIG', 'SHEEP', 'GOAT', 'POULTRY']),
    raisedIn: countryValuesSchema,
    slaughteredIn: countryValueSchema,
    slaughterAgeMonths: z.number().min(0).nullable(),
    slaughterLiveWeightKg: z.number().min(0).nullable(),
    residenceThresholdMet: z.boolean(),
    mincedOrTrimmings: z.boolean(),
    thirdCountryInfoUnavailable: z.boolean(),
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.FISHERY),
    scientificName: z.string().max(200),
    productionMethod: z.enum(FISHERY_PRODUCTION_METHODS),
    faoArea: nullableFaoAreaSchema,
    faoSubArea: nullableFaoAreaSchema,
    waterBody: waterBodySchema.nullable(),
    countryCode: nullableMarketCountrySchema,
    gearCategory: z.enum(FISHERY_GEAR_CATEGORIES).nullable(),
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.HONEY),
    harvestCountries: z
      .array(
        z.object({
          countryCode: marketCountrySchema,
          sharePercent: z.number().min(0).max(100),
        }),
      )
      .max(20),
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.OLIVE_OIL),
    category: z.enum(['EXTRA_VIRGIN', 'VIRGIN', 'COMPOSED', 'POMACE']),
    oliveHarvestPlace: nullableCountryValueSchema,
    millPlace: nullableCountryValueSchema,
    blend: z.enum(['SINGLE', 'EU', 'NON_EU', 'EU_AND_NON_EU']).nullable(),
    protectedDesignation: protectedDesignationSchema.nullable(),
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.IT_MILK_DAIRY),
    milkingCountry: countryValueSchema,
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA),
    wheatGrowingCountry: countryValueSchema,
    millingCountry: countryValueSchema,
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.IT_RICE),
    growingCountry: countryValueSchema,
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.IT_TOMATO),
    growingCountry: countryValueSchema,
  }),
  z.object({
    regime: z.literal(PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK),
    bornIn: countryValueSchema,
    raisedIn: countryValueSchema,
    slaughteredIn: countryValueSchema,
  }),
  z.object({ regime: z.literal(PrepackagedVerticalOriginRegime.OTHER_REQUIRES_REVIEW) }),
]);

export type IngredientVerticalOriginEntrySchema = z.infer<
  typeof ingredientVerticalOriginEntrySchema
>;

export function deriveVerticalOriginRegimes(
  entries: readonly { regime: PrepackagedVerticalOriginRegime }[],
): PrepackagedVerticalOriginRegime[] {
  const regimes = new Set(entries.map((entry) => entry.regime));
  return PREPACKAGED_VERTICAL_ORIGIN_REGIMES.filter((regime) => regimes.has(regime));
}

function mergedEntryOf(
  regime: PrepackagedVerticalOriginRegime,
  ingredientEntry: IngredientVerticalOriginEntry | undefined,
  projectEntry: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!ingredientEntry) {
    return projectEntry ?? {};
  }
  if (!projectEntry) {
    return { ...ingredientEntry };
  }
  const ingredientFields = Object.fromEntries(
    INGREDIENT_VERTICAL_ORIGIN_FIELDS[regime].map((fieldName) => [
      fieldName,
      ingredientEntry[fieldName as keyof IngredientVerticalOriginEntry],
    ]),
  );
  return { ...ingredientFields, ...projectEntry };
}

/** Project override wins because label-level form is last explicit operator statement.
 * ponytail: one entry per regime, canonical order — the label declares ONE
 * origin statement per product category, so the first ingredient carrying a
 * regime is its representative; multi-origin blends need an explicit
 * project-level entry. */
export function mergeVerticalOriginEntries(
  projectEntries: readonly Record<string, unknown>[],
  ingredientEntries: readonly IngredientVerticalOriginEntry[],
): Record<string, unknown>[] {
  const projectByRegime = new Map<string, Record<string, unknown>>();
  for (const projectEntry of projectEntries) {
    if (typeof projectEntry.regime === 'string' && !projectByRegime.has(projectEntry.regime)) {
      projectByRegime.set(projectEntry.regime, projectEntry);
    }
  }
  const ingredientFieldsByRegime = new Map<
    PrepackagedVerticalOriginRegime,
    IngredientVerticalOriginEntry
  >();
  for (const ingredientEntry of ingredientEntries) {
    if (!ingredientFieldsByRegime.has(ingredientEntry.regime)) {
      ingredientFieldsByRegime.set(ingredientEntry.regime, ingredientEntry);
    }
  }
  const mergedEntries: Record<string, unknown>[] = [];
  for (const regime of PREPACKAGED_VERTICAL_ORIGIN_REGIMES) {
    const projectEntry = projectByRegime.get(regime);
    if (!ingredientFieldsByRegime.has(regime) && !projectEntry) {
      continue;
    }
    mergedEntries.push(mergedEntryOf(regime, ingredientFieldsByRegime.get(regime), projectEntry));
  }
  return mergedEntries;
}
